import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { ApiError, apiFetch, resolveApiUrl } from "@/api/client";

// Portal-launcher payload for the ATTMOS Suite Unification surface
// (M9.5). Mirrors the wire shape in
// ``src/flowstack/server/routes/apps.py::PortalView``.
//
// Phase A: launcher only — clicking a card opens the portal in a new
// tab (``window.open(url, '_blank', 'noopener,noreferrer')``). Iframe
// embedding is deferred to P1.0+; the ``embed`` field is preserved so
// a later phase can switch on it without API churn.

export interface PortalEmbed {
  hostname: string;
  path: string;
}

export interface PortalOwnerContact {
  email: string;
  team: string;
}

export interface PortalIcon {
  path: string;
  /**
   * Optional dark-theme variant; falls back to ``path`` when omitted.
   * Same pattern as the FlowStack Header's theme-aware logo swap.
   *
   * Always ``string`` or explicitly ``null`` on the wire — never
   * ``undefined``. Per Rupert PR-360 r0 #3: the Python ``Icon`` model
   * defaults to ``None`` and serializes as JSON ``null``, so the
   * field is always present. Dropping the ``?`` modifier keeps call
   * sites checking exactly one absence-shape.
   */
  path_dark: string | null;
  alt: string;
}

/**
 * Resolve which icon source to use for the current FlowStack theme.
 * Extracted as a pure helper (no React hooks) so the theme-swap
 * behavior can be unit-tested deterministically — per Rupert PR-360
 * r0 #2 (the SPA's Zustand theme store can't be driven from a
 * component test, but this helper can be).
 */
export function resolveIconSrc(icon: PortalIcon, resolvedTheme: "light" | "dark"): string {
  const path = resolvedTheme === "dark" && icon.path_dark ? icon.path_dark : icon.path;
  return `/${path}`;
}

export interface Portal {
  slug: string;
  display_name: string;
  description: string;
  tagline: string | null;
  embed: PortalEmbed;
  icon: PortalIcon;
  owner_contact: PortalOwnerContact;
}

/**
 * Where an in-app card leads, as the closed pair the server validates
 * (`flowstack.launcher_cards.InAppTarget`). Typed as `string` here because the
 * SPA never trusts it to be closed: {@link inAppHref} maps only the pairs it
 * knows and refuses the rest.
 */
export interface InAppTarget {
  page: string;
  app: string;
}

/** API paths of an in-app card's logo, one per theme (`GET /api/apps/{slug}/icon`). */
export interface InAppCardIcon {
  light_url: string;
  dark_url: string;
}

/**
 * An in-app card: a FlowStack page a bundle declares, opened in the same tab.
 * Mirrors `InAppCardView` in `src/flowstack/server/routes/apps.py`. It has no
 * `embed`: it never names a URL, which is why it travels in its own `in_app`
 * array and `Portal.embed` stays required.
 */
export interface InAppCard {
  slug: string;
  display_name: string;
  description: string;
  tagline: string;
  target: InAppTarget;
  icon: InAppCardIcon;
  owner_contact: PortalOwnerContact;
  source_bundle: string;
}

export interface InAppCatalogEntry extends InAppCard {
  entitled: boolean;
}

export interface AppsListResponse {
  apps: Portal[];
  /** Absent from an API that predates in-app cards; read as empty. */
  in_app?: InAppCard[];
}

export interface PortalCatalogEntry extends Portal {
  /**
   * Whether the current session is entitled to launch this portal.
   * Powers the M9.5 Phase E entitlement panel in Settings: entitled
   * portals get an "Open" link; non-entitled get a "Request access"
   * mailto pointing at the portal's owner_contact.email.
   */
  entitled: boolean;
}

export interface AppsCatalogResponse {
  apps: PortalCatalogEntry[];
  /** Absent from an API that predates in-app cards; read as empty. */
  in_app?: InAppCatalogEntry[];
}

/** Caches the whole {@link AppsListResponse}; read it through the hooks below. */
export const APPS_QUERY_KEY = ["apps", "list"] as const;
/** Caches the whole {@link AppsCatalogResponse}. */
export const APPS_CATALOG_QUERY_KEY = ["apps", "catalog"] as const;

/**
 * Allowed eTLD+1 suffix for portal launch URLs. Mirrors the
 * server-side gate in ``flowstack/portals/_schema.py`` — defense in
 * depth so a manifest that somehow bypassed the server validator
 * (forward-compat schema drift, server-side bug) can't render an
 * anchor that points off-domain.
 */
const ATTMOS_DOMAIN_SUFFIX = ".attmosdiscovery.com";

/**
 * Build the absolute URL the launcher card opens. Always HTTPS — every
 * sibling portal is on the ``attmosdiscovery.com`` eTLD+1 and serves
 * TLS in production. Local-dev portals running over HTTP would need a
 * separate code path; not in M9.5 scope.
 *
 * Returns `null` when the manifest's embed target fails a client-side
 * sanity check (non-DNS-label hostname, off-domain suffix, or a URL
 * that doesn't round-trip through `new URL()` with an https scheme).
 * Per Rupert PR-351 r0 #3: defense-in-depth against a `javascript:` /
 * `data:` URI somehow reaching the client through a future
 * forward-compat schema variant. `target="_blank"` with a
 * `javascript:` href can still execute in some browsers even with
 * `rel="noopener noreferrer"`.
 */
export function portalLaunchUrl(portal: Portal): string | null {
  const candidate = `https://${portal.embed.hostname}${portal.embed.path}`;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (!url.hostname.endsWith(ATTMOS_DOMAIN_SUFFIX)) return null;
  // Reject the bare apex as a portal host — same defense-in-depth as
  // the server-side validator.
  if (url.hostname === ATTMOS_DOMAIN_SUFFIX.slice(1)) return null;
  // Reject any URL carrying userinfo (`user@host`) — a phishing
  // vector even when the URL parser resolves the trailing hostname
  // correctly. The server-side DNS-label regex already excludes the
  // `@` character, so reaching this branch is forward-compat / bug
  // territory.
  if (url.username !== "" || url.password !== "") return null;
  return url.href;
}

// ---- In-app links ----------------------------------------------------

/** The project slug charset an in-app link accepts. */
const PROJECT_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

/**
 * The in-app pages the SPA knows, keyed by `target.page`, and the apps each
 * accepts, keyed by `target.app`. The SPA owns every path: a card names a page
 * and an app from these closed sets and nothing else.
 */
const IN_APP_PAGES: Readonly<
  Record<string, { apps: readonly string[]; path: (slug: string) => string }>
> = {
  submit_flow: { apps: ["affdo"], path: (slug) => `/projects/${slug}/submit/flow` },
};

/** Characters an in-app href may never contain once built: control and
 * whitespace characters, and the bidi controls that make a link read as
 * something it is not. */
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const UNSAFE_HREF_CHARS = /[\u0000-\u001f\u007f-\u009f\s‎‏‪-‮⁦-⁩]/;

/** True when `slug` is a project the server listed and is in the link charset. */
export function isLinkableProjectSlug(slug: string, projectSlugs: readonly string[]): boolean {
  return PROJECT_SLUG_RE.test(slug) && projectSlugs.includes(slug);
}

/**
 * The ONLY builder of an in-app card's href.
 *
 * `target` comes from the server and `slug` from a project the viewer picked or
 * last used, so neither is trusted: the page must be one {@link IN_APP_PAGES}
 * knows, the app one that page accepts, and the slug one `GET /api/projects`
 * returned (`projectSlugs`) in the link charset. The slug is percent-encoded
 * and the app travels as a query parameter through `URLSearchParams`. The
 * result must then still be a single same-origin path: one leading `/`, no
 * `//`, `\`, `..`, `@`, `#`, control or whitespace character, and an origin
 * equal to this page's. Any failure returns `null`, and the card renders
 * disabled instead of navigating anywhere.
 */
export function inAppHref(
  target: InAppTarget,
  slug: string,
  projectSlugs: readonly string[]
): string | null {
  const page = Object.hasOwn(IN_APP_PAGES, target.page) ? IN_APP_PAGES[target.page] : undefined;
  if (page === undefined || !page.apps.includes(target.app)) return null;
  if (!isLinkableProjectSlug(slug, projectSlugs)) return null;

  const query = new URLSearchParams({ app: target.app }).toString();
  const href = `${page.path(encodeURIComponent(slug))}?${query}`;

  if (!href.startsWith("/") || href.startsWith("//")) return null;
  if (href.includes("//") || href.includes("\\") || href.includes("..")) return null;
  if (href.includes("@") || href.includes("#") || UNSAFE_HREF_CHARS.test(href)) return null;
  try {
    if (new URL(href, window.location.origin).origin !== window.location.origin) return null;
  } catch {
    return null;
  }
  return href;
}

// ---- In-app icons ----------------------------------------------------

/** The exact shape of the icon route the server emits for a card
 * (`_to_card_view` in `routes/apps.py`). */
const CARD_ICON_PATH_RE = /^\/api\/apps\/[a-z][a-z0-9-]{1,31}\/icon\?theme=(light|dark)$/;

/** True when `path` is the API's own card-icon route — the only path
 * {@link useCardIcon} fetches with credentials. */
export function isCardIconPath(path: string): boolean {
  return CARD_ICON_PATH_RE.test(path);
}

/** The icon path for the theme on screen. */
export function cardIconPath(icon: InAppCardIcon, resolvedTheme: "light" | "dark"): string {
  return resolvedTheme === "dark" ? icon.dark_url : icon.light_url;
}

async function fetchCardIcon(path: string): Promise<Blob> {
  // `path` is server-response data crossing into a credentialed fetch, so it
  // is pinned to the icon route's shape first (the `apiDownload` posture).
  if (!isCardIconPath(path)) {
    throw new Error("fetchCardIcon: refusing a path that is not a card-icon route on this API");
  }
  const response = await fetch(await resolveApiUrl(path), {
    credentials: "include",
    headers: { Accept: "image/png" },
  });
  if (!response.ok) {
    throw new ApiError(response.status, {
      error_code: "icon_unavailable",
      message: `Icon ${path} answered ${response.status}`,
    });
  }
  const blob = await response.blob();
  if (blob.type !== "image/png") {
    throw new ApiError(response.status, {
      error_code: "icon_unavailable",
      message: `Icon ${path} is not a PNG`,
    });
  }
  return blob;
}

/**
 * An in-app card's logo as a `blob:` URL, or `null` until it loads (or when it
 * cannot).
 *
 * The icon route is on the API origin and needs the session cookie, so a
 * relative `/api/...` path in an `<img src>` would ask the WEBAPP origin and
 * get the SPA shell. The PNG is fetched with credentials instead, and the
 * browser's own HTTP cache revalidates it against the route's `ETag`
 * (`Cache-Control: private, no-cache`), so every view re-runs the server's
 * entitlement check. The object URL is revoked when the path changes or the
 * component unmounts.
 */
export function useCardIcon(path: string): string | null {
  const { data: blob } = useQuery({
    queryKey: ["apps", "icon", path],
    queryFn: () => fetchCardIcon(path),
    staleTime: 60_000,
    retry: false,
  });
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    // No object URLs (a non-browser renderer) means no logo: the card shows
    // its name instead, as when the icon cannot load.
    if (blob === undefined || typeof URL.createObjectURL !== "function") {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return url;
}

// ---- Queries ---------------------------------------------------------

// Each key caches the WHOLE response, and every hook reads its array through
// `select` (the `useProjects` pattern): the portals and the in-app cards are
// one request and always one snapshot of the viewer's entitlements.

function fetchAppsList(): Promise<AppsListResponse> {
  return apiFetch<AppsListResponse>("/api/apps");
}

function fetchAppsCatalog(): Promise<AppsCatalogResponse> {
  return apiFetch<AppsCatalogResponse>("/api/apps/catalog");
}

function selectApps<T>(response: { apps: T[] }): T[] {
  return response.apps;
}

/** An API that predates in-app cards omits the array; read it as empty. */
function selectInApp<T>(response: { in_app?: T[] }): T[] {
  return response.in_app ?? [];
}

export function useApps() {
  return useQuery({
    queryKey: APPS_QUERY_KEY,
    queryFn: fetchAppsList,
    select: selectApps<Portal>,
    // Same 60s staleTime as the session query — entitlement changes
    // are bounded by the session refresh cadence anyway (anchor §5.1).
    staleTime: 60_000,
  });
}

/** The in-app cards this session is entitled to, from the same `/api/apps`
 * response as {@link useApps}. Empty from an API that predates them. */
export function useInAppCards() {
  return useQuery({
    queryKey: APPS_QUERY_KEY,
    queryFn: fetchAppsList,
    select: selectInApp<InAppCard>,
    staleTime: 60_000,
  });
}

/**
 * Full catalog of portals + per-portal entitlement state. Powers the
 * Settings entitlement panel (Phase E / #347). Distinct from
 * {@link useApps} which returns only the entitled subset.
 */
export function useAppsCatalog() {
  return useQuery({
    queryKey: APPS_CATALOG_QUERY_KEY,
    queryFn: fetchAppsCatalog,
    select: selectApps<PortalCatalogEntry>,
    staleTime: 60_000,
  });
}

/** Every in-app card with its `entitled` state, from the same
 * `/api/apps/catalog` response as {@link useAppsCatalog}. */
export function useInAppCatalog() {
  return useQuery({
    queryKey: APPS_CATALOG_QUERY_KEY,
    queryFn: fetchAppsCatalog,
    select: selectInApp<InAppCatalogEntry>,
    staleTime: 60_000,
  });
}
